import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  createAdminContentItem,
  fetchAdminContentModule,
  updateAdminContentItem,
} from '../../services/contentAdminApi';
import { getApiErrorMessage } from '../../services/apiUtils';
import type { AdminContentModuleResponse, PromoPopupItem } from '../../types/content';
import { fileToPayload } from '../../utils/fileUpload';
import {
  adminContentCardSx,
  adminHeroSx,
  adminMetricIconSx,
  adminSoftChipSx,
  adminSummaryCardSx,
  adminTableCardSx,
} from '../../components/admin/adminVisuals';

function formatDate(value?: string | null) {
  if (!value) return '--';
  return new Date(value).toLocaleString('pt-BR');
}

const emptyForm = {
  title: '',
  alt: '',
  href: '',
  imageUrl: '',
  buttonLabel: 'Saiba mais',
  isActive: true,
};

export default function AdminPromoPopupPage() {
  const [item, setItem] = useState<PromoPopupItem | null>(null);
  const [moduleMeta, setModuleMeta] = useState<AdminContentModuleResponse<PromoPopupItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [form, setForm] = useState(emptyForm);

  const loadItem = useCallback(async (options?: { clearFeedback?: boolean }) => {
    setIsLoading(true);
    if (options?.clearFeedback !== false) {
      setFeedback(null);
    }

    try {
      const response = await fetchAdminContentModule<PromoPopupItem>('promoPopup');
      const currentItem = response.items[0] || null;

      setItem(currentItem);
      setModuleMeta(response);
      setForm(
        currentItem
          ? {
              title: currentItem.title || '',
              alt: currentItem.alt || currentItem.title || '',
              href: currentItem.href || '',
              imageUrl: currentItem.imageUrl || '',
              buttonLabel: currentItem.buttonLabel || 'Saiba mais',
              isActive: currentItem.isActive,
            }
          : emptyForm
      );
      setSelectedFile(null);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Nao foi possivel carregar a configuracao do popup.'),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadItem();
  }, [loadItem]);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const previewUrl = selectedPreviewUrl || form.imageUrl.trim();

  const handleSave = async () => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const payload: Record<string, unknown> = {
        title: form.title,
        alt: form.alt || form.title,
        href: form.href,
        imageUrl: form.imageUrl,
        buttonLabel: form.buttonLabel,
        isActive: form.isActive,
      };

      if (selectedFile) {
        payload.file = await fileToPayload(selectedFile);
      }

      if (item) {
        await updateAdminContentItem<PromoPopupItem>('promoPopup', item.id, payload);
      } else {
        await createAdminContentItem<PromoPopupItem>('promoPopup', payload);
      }

      await loadItem({ clearFeedback: false });
      setFeedback({ type: 'success', message: 'Popup atualizado com sucesso.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Nao foi possivel salvar a configuracao do popup.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled =
    isSubmitting ||
    !form.title.trim() ||
    !form.href.trim() ||
    !form.buttonLabel.trim() ||
    (!selectedFile && !form.imageUrl.trim());

  return (
    <Stack spacing={3}>
      <Box sx={adminHeroSx}>
        <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Comunicacao da homepage" variant="outlined" sx={adminSoftChipSx} />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#16324f' }}>
            Popup promocional
          </Typography>
          <Typography sx={{ maxWidth: 860, color: 'rgba(22, 50, 79, 0.88)' }}>
            Controle a exibicao do popup do site, com status, imagem, texto do botao e link de destino.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
            <Chip
              label={form.isActive ? 'Popup ativo' : 'Popup inativo'}
              color={form.isActive ? 'success' : 'default'}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)', fontWeight: 700 }}
            />
            <Chip
              label={`Ultima atualizacao: ${formatDate(moduleMeta?.updatedAt)}`}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)' }}
            />
            <Chip
              label={`Ultima publicacao: ${formatDate(moduleMeta?.lastPublishedAt || moduleMeta?.publishedAt)}`}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)' }}
            />
          </Stack>
        </Stack>
      </Box>

      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

      {moduleMeta?.lastPublicationStatus && moduleMeta.lastPublicationStatus !== 'published' && (
        <Alert severity="warning">
          {moduleMeta.lastPublicationError ||
            'Nao foi possivel concluir a ultima atualizacao. Revise os dados e tente novamente.'}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(37, 99, 235, 0.12)', '#1d4ed8')}>
                <CampaignRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {form.isActive ? 'Ativo' : 'Inativo'}
              </Typography>
              <Typography color="text.secondary">Status de exibicao</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(245, 158, 11, 0.16)', '#b45309')}>
                <ImageRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {selectedFile ? 'Upload' : form.imageUrl ? 'URL' : '--'}
              </Typography>
              <Typography color="text.secondary">Origem da imagem</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(14, 165, 233, 0.14)', '#0369a1')}>
                <LinkRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {form.href ? 'Configurado' : 'Pendente'}
              </Typography>
              <Typography color="text.secondary">Link de destino</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={adminTableCardSx}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ color: '#16324f' }}>
                Configuracao do popup
              </Typography>
              <Typography color="text.secondary">
                A alteracao e publicada no cache publico sem alterar os demais modulos do painel.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Atualizar">
                <span>
                  <IconButton onClick={() => void loadItem()} disabled={isLoading || isSubmitting}>
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                onClick={() => void handleSave()}
                disabled={isSaveDisabled}
                sx={{ borderRadius: 999 }}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar popup'}
              </Button>
            </Stack>
          </Stack>

          {isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Carregando configuracao...</Typography>
            </Stack>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
                      />
                    }
                    label="Exibir popup no site"
                  />

                  <TextField
                    label="Titulo interno"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                    fullWidth
                    required
                  />

                  <TextField
                    label="Texto alternativo da imagem"
                    value={form.alt}
                    onChange={(event) => setForm((current) => ({ ...current, alt: event.target.value }))}
                    fullWidth
                    helperText="Usado para acessibilidade. Se ficar vazio, o titulo sera usado."
                  />

                  <TextField
                    label="Texto do botao"
                    value={form.buttonLabel}
                    onChange={(event) => setForm((current) => ({ ...current, buttonLabel: event.target.value }))}
                    fullWidth
                    required
                  />

                  <TextField
                    label="Link do popup"
                    value={form.href}
                    onChange={(event) => setForm((current) => ({ ...current, href: event.target.value }))}
                    fullWidth
                    required
                    helperText="Informe uma rota do site iniciada com / ou uma URL completa."
                  />

                  <TextField
                    label="Endereco da imagem"
                    value={form.imageUrl}
                    onChange={(event) => setForm((current) => ({ ...current, imageUrl: event.target.value }))}
                    fullWidth
                    helperText="Se houver envio de imagem, ele substitui o endereco informado."
                  />

                  <Button component="label" variant="outlined" sx={{ borderRadius: 999 }}>
                    {selectedFile ? `Imagem selecionada: ${selectedFile.name}` : 'Escolher imagem'}
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                    />
                  </Button>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card sx={{ ...adminContentCardSx, height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 2, color: '#16324f' }}>
                      Previa do popup
                    </Typography>
                    {previewUrl ? (
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: '#e6ebf2',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box
                            component="img"
                            src={previewUrl}
                            alt={form.alt || form.title || 'Previa do popup'}
                            sx={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain' }}
                          />
                        </Box>
                        {form.href && (
                          <Button variant="contained" href={form.href} target="_blank" sx={{ borderRadius: 999 }}>
                            {form.buttonLabel || 'Saiba mais'}
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <Alert severity="info">Informe uma imagem ou selecione um arquivo para visualizar o popup.</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
